/**
 * NotifyrManager.ts - Notification Manager
 * 
 * CHANGE HISTORY:
 * - 251031_0826 danielbeer: Complete rewrite with proper TypeScript
 * - 251030_0950 danielbeer: Added Mattermost and Mailr channels
 * - 251018_0831 danielbeer: Initial creation
 * 
 * Universal notification system with multiple delivery channels
 */

import { Notification } from './models/Notification';
import type { LoggrManager } from '@opcentral/loggr';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';
export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';
export type NotificationChannel = 'in-app' | 'email' | 'sms' | 'webhook' | 'push' | 'mattermost';
export type NotificationStatus = 'pending' | 'sent' | 'delivered' | 'failed' | 'read';

export interface NotificationAction {
  label: string;
  action: string;
  url?: string;
  style?: 'primary' | 'secondary' | 'danger';
  data?: Record<string, any>;
}

export interface NotificationConfig {
  userId: string;
  title: string;
  message: string;
  type?: NotificationType;
  priority?: NotificationPriority;
  channels?: NotificationChannel[];
  actions?: NotificationAction[];
  icon?: string;
  url?: string;
  data?: Record<string, any>;
  expiresAt?: string;
}

export interface NotifyrOptions {
  enableEmail?: boolean;
  enableSMS?: boolean;
  enableWebhook?: boolean;
  enablePush?: boolean;
  enableMattermost?: boolean;
  maxNotifications?: number;
  mattermostUrl?: string;
  mattermostToken?: string;
  mailrApiUrl?: string;
  mailrApiKey?: string;
  mailrFrom?: string;
}

export interface GetNotificationsOptions {
  unreadOnly?: boolean;
  type?: NotificationType;
  priority?: NotificationPriority;
  limit?: number;
}

/**
 * NotifyrManager - Core notification management
 * 
 * Features:
 * - Multiple delivery channels (in-app, email, SMS, webhook, push, Mattermost)
 * - Priority levels and types
 * - Read/unread tracking
 * - Notification history
 * - Action buttons
 * - Expiration
 */
export class NotifyrManager {
  private options: NotifyrOptions;
  private notifications: Map<string, Notification[]>;
  private listeners: Set<(notification: Notification) => void>;
  private channels: Map<string, any>;
  private loggr?: LoggrManager;

  constructor(options: NotifyrOptions = {}, loggr?: LoggrManager) {
    this.options = {
      enableEmail: true,
      enableSMS: false,
      enableWebhook: false,
      enablePush: false,
      enableMattermost: false,
      maxNotifications: 100,
      ...options
    };
    
    this.notifications = new Map();
    this.listeners = new Set();
    this.channels = new Map();
    this.loggr = loggr;

    this.loggr?.info('NotifyrManager initialized', 'notifyr', { options: this.options });
  }

  /**
   * Send a notification
   */
  async send(config: NotificationConfig): Promise<Notification> {
    const notification = new Notification({
      ...config,
      type: config.type || 'info',
      priority: config.priority || 'normal',
      channels: config.channels || ['in-app'],
      status: 'pending'
    });

    // Store notification
    const userNotifications = this.notifications.get(config.userId) || [];
    userNotifications.push(notification);
    
    // Enforce max notifications limit
    if (this.options.maxNotifications && userNotifications.length > this.options.maxNotifications) {
      userNotifications.shift(); // Remove oldest
    }
    
    this.notifications.set(config.userId, userNotifications);

    // Send through channels
    await this.sendThroughChannels(notification);

    // Notify listeners
    this.notifyListeners(notification);

    this.loggr?.info('Notification sent', 'notifyr', { 
      id: notification.id, 
      userId: config.userId, 
      channels: config.channels 
    });

    return notification;
  }

  /**
   * Send notification through configured channels
   */
  private async sendThroughChannels(notification: Notification): Promise<void> {
    const channels = notification.channels || ['in-app'];
    
    for (const channel of channels) {
      try {
        const handler = this.channels.get(channel);
        if (handler && typeof handler.send === 'function') {
          await handler.send(notification);
          this.loggr?.debug(`Notification sent via ${channel}`, 'notifyr', { id: notification.id });
        } else if (channel !== 'in-app') {
          this.loggr?.warn(`No handler for channel: ${channel}`, 'notifyr', { id: notification.id });
        }
      } catch (error) {
        this.loggr?.error(`Failed to send via ${channel}`, 'notifyr', { 
          id: notification.id, 
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
    
    notification.markAsSent();
  }

  /**
   * Get notifications for a user
   */
  getNotifications(userId: string, options: GetNotificationsOptions = {}): Notification[] {
    let notifications = this.notifications.get(userId) || [];
    
    // Filter by read status
    if (options.unreadOnly) {
      notifications = notifications.filter(n => !n.read);
    }
    
    // Filter by type
    if (options.type) {
      notifications = notifications.filter(n => n.type === options.type);
    }
    
    // Filter by priority
    if (options.priority) {
      notifications = notifications.filter(n => n.priority === options.priority);
    }
    
    // Limit
    if (options.limit) {
      notifications = notifications.slice(0, options.limit);
    }
    
    return notifications;
  }

  /**
   * Get unread count for a user
   */
  getUnreadCount(userId: string): number {
    const notifications = this.notifications.get(userId) || [];
    return notifications.filter(n => !n.read).length;
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string, userId: string): boolean {
    const notifications = this.notifications.get(userId) || [];
    const notification = notifications.find(n => n.id === notificationId);
    
    if (notification) {
      notification.markAsRead();
      this.loggr?.debug('Notification marked as read', 'notifyr', { id: notificationId, userId });
      return true;
    }
    
    return false;
  }

  /**
   * Mark all notifications as read for a user
   */
  markAllAsRead(userId: string): number {
    const notifications = this.notifications.get(userId) || [];
    let count = 0;
    
    notifications.forEach(n => {
      if (!n.read) {
        n.markAsRead();
        count++;
      }
    });
    
    this.loggr?.info('All notifications marked as read', 'notifyr', { userId, count });
    return count;
  }

  /**
   * Delete a notification
   */
  deleteNotification(notificationId: string, userId: string): boolean {
    const notifications = this.notifications.get(userId) || [];
    const index = notifications.findIndex(n => n.id === notificationId);
    
    if (index !== -1) {
      notifications.splice(index, 1);
      this.notifications.set(userId, notifications);
      this.loggr?.debug('Notification deleted', 'notifyr', { id: notificationId, userId });
      return true;
    }
    
    return false;
  }

  /**
   * Clear all notifications for a user
   */
  clearAll(userId: string): number {
    const notifications = this.notifications.get(userId) || [];
    const count = notifications.length;
    this.notifications.set(userId, []);
    this.loggr?.info('All notifications cleared', 'notifyr', { userId, count });
    return count;
  }

  /**
   * Register a channel handler
   */
  registerChannel(name: string, handler): void {
    this.channels.set(name, handler);
    this.loggr?.info('Channel registered', 'notifyr', { name });
  }

  /**
   * Subscribe to notification events
   */
  subscribe(listener: (notification: Notification) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(notification: Notification): void {
    this.listeners.forEach(listener => {
      try {
        listener(notification);
      } catch (error) {
        this.loggr?.error('Listener error', 'notifyr', { 
          error: error instanceof Error ? error.message : String(error)
        });
      }
    });
  }

  /**
   * Get all notifications (for admin/debugging)
   */
  getAllNotifications(): Record<string, Notification[]> {
    const result: Record<string, Notification[]> = {};
    this.notifications.forEach((notifications, userId) => {
      result[userId] = notifications.filter(n => !n.isExpired());
    });
    return result;
  }

  /**
   * Export to JSON
   */
  toJSON(): any {
    return {
      options: this.options,
      notifications: Object.fromEntries(
        Array.from(this.notifications.entries()).map(([userId, notifications]) => [
          userId,
          notifications.map(n => n.toJSON())
        ])
      )
    };
  }

  /**
   * Load from JSON
   */
  loadFromJSON(data): void {
    if (data.notifications) {
      Object.entries(data.notifications).forEach(([userId, notifications]: [string, any]) => {
        this.notifications.set(
          userId,
          (notifications as any[]).map(n => Notification.fromJSON(n))
        );
      });
    }
  }
}

// Singleton instance
let instance: NotifyrManager | null = null;

/**
 * Get singleton instance
 */
export function getNotifyr(options?: NotifyrOptions, loggr?: LoggrManager): NotifyrManager {
  if (!instance) {
    instance = new NotifyrManager(options, loggr);
  }
  return instance;
}

export default NotifyrManager;
